import { type GridRenderCellParams } from "@mui/x-data-grid-pro";
import axios, { type AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import navigationItemReferences from "../../../constants/navigation-item-references";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../hooks/useDatagridSorting";
import useToastAlert from "../../../hooks/useToastAlert";
import { selectOrganizationId } from "../../../redux/userSlice";
import CoAppDatagridHeader from "../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../global/components/datagrid/CoAppStandardDataGrid";
import useCoAppDataGridFilters from "../../global/components/datagrid/useCoAppDataGridFilters";
import SettingsDataGridSelectionRow from "../SettingsDataGridSelectionRow";
import { getPluginManagementColumns, type PluginRow } from "./columns/plugin-management-columns";

function openPluginEditPageAdapter(
	openResourceEditPage: ReturnType<typeof useCoAppNavigation>["openResourceEditPage"],
) {
	return (params: GridRenderCellParams<PluginRow>, openedFrom: number, parentName: string) => {
		const { row } = params;
		openResourceEditPage(
			{
				id: params.id == null ? undefined : String(params.id),
				row: {
					name: row.name,
					active: typeof row.active === "boolean" ? row.active : undefined,
					id: row.id == null ? undefined : String(row.id),
				},
			},
			openedFrom,
			parentName,
		);
	};
}

function openPluginHomePageAdapter(
	handleOpenResourceHomePage: ReturnType<typeof useCoAppNavigation>["handleOpenResourceHomePage"],
) {
	return (navIndex: number, resourceName: string, params: GridRenderCellParams<PluginRow>) => {
		const { row } = params;
		handleOpenResourceHomePage(navIndex, resourceName, {
			id: params.id == null ? undefined : String(params.id),
			name: row.name,
			isActive: typeof row.active === "boolean" ? row.active : undefined,
			platformId: row.platformId,
			row: {
				name: row.name,
				active: typeof row.active === "boolean" ? row.active : undefined,
				isCustomerManaged: row.isCustomerManaged === true,
				platformId: row.platformId,
			},
		} as Parameters<ReturnType<typeof useCoAppNavigation>["handleOpenResourceHomePage"]>[2]);
	};
}

interface Plugin {
	id: string | number;
	name: string;
	pluginId?: string;
	[key: string]: unknown;
}

export default function PluginManagement() {
	const { handleToastAlert } = useToastAlert();
	const authHeader = useAuthHeader();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("plugins");
	const { openResourceEditPage, handleOpenResourceHomePage } = useCoAppNavigation();
	const openPluginEditPage = openPluginEditPageAdapter(openResourceEditPage);
	const openPluginHomePage = openPluginHomePageAdapter(handleOpenResourceHomePage);
	const organizationId = useSelector(selectOrganizationId);
	const [plugins, setPlugins] = useState<Plugin[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	const handlePluginStatusToggleChange = (pluginId: string | number, state: boolean, platformId: string | number | undefined) => {
		const data = {
			patchData: {
				active: !state
			},
			organizationId: organizationId,
			platformId: platformId as string
		};
		axios.patch(apiRoutes.patchPlugin(pluginId as unknown as string), data, { headers: authHeader })
			.then(() => {
				initPluginManagement();
			})
			.catch((error: AxiosError) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, state ? messages.PLUGIN_DISABLED_ERROR_MSG : messages.PLUGIN_ENABLED_ERROR_MSG);
			});
	};

	const columns = useMemo(() => (
		getPluginManagementColumns(
			handlePluginStatusToggleChange,
			openPluginEditPage,
			openPluginHomePage,
			navigationItemReferences
		)
	), [navigationItemReferences]);

	const initPluginManagement = () => {
		axios.get(apiRoutes.getPlatformsAndPlugins(organizationId as unknown as string), { headers: authHeader })
			.then((response) => {
				const platforms = response.data;
				const plugins: Plugin[] = [];
				platforms.forEach((platform: any) => {
					if (!platform.plugins) {
						return;
					}
					platform.plugins.forEach((plugin: any) => {
						plugins.push({
							id: plugin.id,
							name: plugin.name,
							groups: plugin.groups.map((group: any) => group),
							active: plugin.active,
							isCustomerManaged: plugin.isCustomerManaged,
							endpoint: plugin.endpoint,
							apiKey: plugin.apiKey,
							platformId: platform.id,
							platformName: platform.name
						});
					});
				});
				setPlugins(plugins);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});

	};

	useEffect(() => {
		initPluginManagement();
		handleInitializeSortModel();
	}, []);

	const { gridFilterModel, onFilterModelChange, quickFilterSearchProps, apiRef, visibleRowCount, filterControl, filterButtonEl } = useCoAppDataGridFilters(plugins, loading);
	return (
		<>
			<CoAppDatagridHeader
				iconKind="plugins"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				title="Plugins"
				addIsPresent={false}
				editIsPresent={false}
				deleteIsPresent={false}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				columns={columns}
				rows={plugins}
				pinnedColumns={["actions"]}
				allowSelection={false}
				targetResource="plugins"
				gridKey="plugins"
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun="plugin"
						visibleCount={visibleRowCount}
						selectedCount={0}
						loading={loading}
						onClearSelection={() => {}}
					/>
				}
			/>
		</>
	);
}